import {
  Box,
  InputAdornment,
  SelectChangeEvent,
  Stack,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DateView } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
// import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  format,
  isAfter,
  isBefore,
  isEqual,
  lastDayOfMonth
} from "date-fns";
import it from "date-fns/locale/it";
import { AlertTriangle, Calendar, ChevronLeft, ChevronRight, CircleCheck, TriangleAlert, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AvailableHoursTypography,
  DescriptionTypography,
  FormLabelTypography,
  FormSubLabelTypography,
  OpenCalendarArrow,
  TitleTypography
} from "./CalendarCard.style";
import { SelectTime } from "./SmartLearningSelect.component";
import { ButtonDigited } from "../../components/Button/Button.variants";
import { Icon } from "../../components/Icon";
import { Spinner } from "../../components/Spinner";
import { useContentQuery } from "../../queries";
import {
  type SlotsResponseType,
  useSlotsQuery,
  useSlotsPutQuery
} from "../../queries/smartLearning";
import { useLanguageStore } from "../../stores";
import type {
  DayToHighlight,
  LearningObject,
  MetaData
} from "../../types";
import { switchLocaleImport } from "../../utils/dateFnsLocale";

export function SmartLearningModalEditContent({
  end,
  onClose,
  selectedDate,
  slotId,
  start
}: {
    end: string
    onClose?: () => void
    selectedDate: Date
    slotId: number
    start: string
}) {

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [selected, setSelected] = useState<Date>(selectedDate);
  const [isOpen, setIsOpen] = useState(false);

  const [fromTime, setFromTime] = useState<string>(start);
  const [toTime, setToTime] = useState<string>(end);
  const [startDate, setStartDate] = useState<string | undefined>(
    format(selected, `yyyy-MM-dd'T'${fromTime}:00.000xxx`)
  );
  const [endDate, setEndDate] = useState<string | undefined>(
    format(selected, `yyyy-MM-dd'T'${toTime}:00.000xxx`)
  );
  const [conflictText, setConflictText] = useState(false);

  const language = useLanguageStore(state => state.language)?.id;

  const [locale, setLocale] = useState<Locale>(it);

  useEffect(() => {
    (async () => {
      try {
        const newLocale = await switchLocaleImport(language);
        setLocale(newLocale.default);
      } catch(err) {
        // eslint-disable-next-line no-console
        console.error("import date-fns locale:", err);
      }
    })();
  }, [language]);

  const { data } = useSlotsQuery({
    enabled: true,
    endDate: format(selected, "yyyy-MM-dd'T23:59:00.000Z"),
    month: format(selected, "MM"),
    startDate: format(selected, "yyyy-MM-dd'T00:00:00.000Z"),
    year: format(selected, "yyyy")
  });


  //calculate available hours
  let availableHours: number | undefined = undefined;
  if(data?.hours_available != undefined) {
    const splitStart = start.split(":");
    const startMinutes = Number(splitStart[0]) * 60 + Number(splitStart[1]);
    const splitEnd = end.split(":");
    const endMinutes = Number(splitEnd[0]) * 60 + Number(splitEnd[1]);
    const diff = endMinutes - startMinutes;
    availableHours = (diff + (data?.hours_available * 60)) / 60;
  }

  //learnerAgenda
  const learnerAgendaEndDate = format(lastDayOfMonth(selected), "yyyy-MM-dd");
  const learnerAgendaSelected = format(new Date(selected ?? ""), "yyyy-MM-dd");
  const learnerAgendaStartDate = format(selected, "yyyy-MM-01");
  const {
    data: learnerAgendaData
  } = useContentQuery<{
    daysToHighlight: DayToHighlight[],
    learningObjects: LearningObject[],
    metadata: MetaData
  }>({
    path: "/learning-catalogue/{corporateId}/{initiativeId}/learnerAgenda?selectedDate={selectedDate}&startDate={startDate}&endDate={endDate}&pageNumber={pageNumber}&pageSize={pageSize}",
    queryKey: [learnerAgendaSelected, learnerAgendaStartDate, learnerAgendaEndDate],

    // additional params
    endDate: learnerAgendaEndDate,
    selectedDate: learnerAgendaSelected,
    startDate: learnerAgendaStartDate
  });

  const {
    mutate: mutateSlots,
    isPending: isLoading,
    isError,
    isIdle
  } = useSlotsPutQuery();

  function startDateSelectOptions(
    data: SlotsResponseType): Array<{timeOption: string,
    bookable:boolean,
    disabled: boolean}> {
    return timeSelectOptions().map((timeOption) => {
      let disabled = false;
      let bookable = true;
      const timezone = format(selected, "xxx");
      const timeOptionDate = new Date(format(selected, `yyyy-MM-dd'T'${timeOption}:00.000${timezone}`));
      data.slots.forEach((slot) => {
        if(slot.id != slotId) {
          const slotStartDate = new Date(slot.start_date);
          const slotEndDate = new Date(slot.end_date);
          if(
            isEqual(timeOptionDate, slotStartDate) ||
          (isAfter(timeOptionDate, slotStartDate) && isBefore(timeOptionDate, slotEndDate))
          ) {
            disabled = true;
          }
        }
      });
      learnerAgendaData?.learningObjects.forEach((learningObject) => {
        if(learningObject.startTime != null && learningObject.endTime != null) {
          /* eslint-disable max-len */
          const startTimeFormatted = `${learningObject.startTime.split(":")[0].padStart(2, "0")}:${learningObject.startTime.split(":")[1].padStart(2, "0")}`;
          const endTimeFormatted = `${learningObject.endTime.split(":")[0].padStart(2, "0")}:${learningObject.endTime.split(":")[1].padStart(2, "0")}`;
          const learningObjectStartDate = new Date(format(selected, `yyyy-MM-dd'T'${startTimeFormatted}:00.000${timezone}`));
          const learningObjectEndDate = new Date(format(selected, `yyyy-MM-dd'T'${endTimeFormatted}:00.000${timezone}`));
          /* eslint-enable max-len */
          if(
            isEqual(timeOptionDate, learningObjectStartDate) ||
            (isAfter(timeOptionDate, learningObjectStartDate) && isBefore(timeOptionDate, learningObjectEndDate))
          ) {
            bookable = false;
          }
        }
      });
      if(isBefore(timeOptionDate, new Date())) {
        disabled = true;
      }
      return {
        bookable:bookable,
        disabled: disabled,
        timeOption: timeOption
      };
    });
  }

  function endDateSelectOptions(data: SlotsResponseType): Array<{
    timeOption: string,
    bookable:boolean,
    disabled: boolean}> {
    return timeSelectOptions().map((timeOption) => {
      let disabled = false;
      let bookable = true;

      const timezone = format(selected, "xxx");
      const timeOptionDate = new Date(format(selected, `yyyy-MM-dd'T'${timeOption}:00.000${timezone}`));
      const selectedStartDate = new Date(startDate ?? "");
      if(isEqual(timeOptionDate, selectedStartDate) || isBefore(timeOptionDate, selectedStartDate)) {
        disabled = true;
      }
      if(
        availableHours != undefined &&
        timeOptionDate.getTime() - selectedStartDate.getTime() > availableHours * 60 * 60 * 1000
      ) {
        disabled = true;
      }
      if(!disabled) {
        data.slots.forEach((slot) => {
          if(slot.id != slotId) {
            const slotStartDate = new Date(slot.start_date);
            if(
              isBefore(selectedStartDate, slotStartDate) &&
            isAfter(timeOptionDate, slotStartDate)
            ) {
              disabled = true;
            }
          }
        });

        learnerAgendaData?.learningObjects.forEach((learningObject) => {
          if(learningObject.startTime != null && learningObject.endTime != null) {
            /* eslint-disable max-len */
            const startTimeFormatted = `${learningObject.startTime.split(":")[0].padStart(2, "0")}:${learningObject.startTime.split(":")[1].padStart(2, "0")}`;
            const learningObjectStartDate = new Date(format(selected, `yyyy-MM-dd'T'${startTimeFormatted}:00.000${timezone}`));
            /* eslint-enable max-len */
            if(
              isBefore(selectedStartDate, learningObjectStartDate) &&
              isAfter(timeOptionDate, learningObjectStartDate)
            ) {
              bookable = false;
            }
          }
        });
      }
      if(isBefore(timeOptionDate, new Date())) {
        disabled = true;
      }
      return {
        bookable:bookable,
        disabled: disabled,
        timeOption: timeOption
      };
    });
  }

  const startOptions = data ? startDateSelectOptions(data) : [];
  const endOptions = data ? endDateSelectOptions(data) : [];
  const { t } = useTranslation();
  const [view, setView] = useState<DateView>("day");

  const currentMonth = format(selected, "MMMM", { locale: locale });
  const labelSwitchView =`${currentMonth} - ${t("calendar_switch_view",{ view:view === "day" ? t("month"):t("year"),view_to_show:view !== "day" ? t("month"):t("year") })}`;
    

  return (
    <>
      { isIdle ?
        <Stack
          gap={ "24px" }
          height={ "100%" }
        >
          { /* header */ }
          <Stack
            tabIndex={ -1 }
            flexDirection={ "row" }
            role="group"
            gap={ "2rem" }
            justifyContent={ "space-between" }
          >
            { /* title and description */ }
            <Stack
              tabIndex={ -1 }
              gap={ "1rem" }
            >
              <TitleTypography
                id="title-to-announce-edit"
                sx={ {
                  "&:focus-visible": {
                    outline: `2px solid ${theme.customColors.headerIcon}`,
                    outlineOffset: "2px",
                    borderRadius: "2px"
                  }
                } }
                tabIndex={ 0 }
                color={ theme.customColors.exploreCardText }>
                { t("edit_slot") }
              </TitleTypography>
              <DescriptionTypography
                sx={ {
                  "&:focus-visible": {
                    outline: `2px solid ${theme.customColors.headerIcon}`,
                    outlineOffset: "2px",
                    borderRadius: "2px"
                  }
                } }
                tabIndex={ 0 }
                color={ theme.customColors.exploreCardText }>
                { t("complete_agenda") }
              </DescriptionTypography>
            </Stack>
            { /* close modal button */ }
            <X 
              type="button"
              size={ "1.5rem" }
              cursor="pointer"
              id="close-modal-icon"
              aria-label={ t("close") }
              onClick={ () => {
                if (onClose) {
                  onClose();
                }
              }
              }
              onKeyDown={ (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  if (onClose) {
                    onClose();
                  }
                }
              } }
              style={ {
                top: "1.5rem",
                right:"1.5rem"
              } }
            />
          </Stack>
          { /* body container */ }
          <Stack
            tabIndex={ -1 }
            gap={ "2rem" }
          >
            { /* inputs */ }
            <Stack
              tabIndex={ -1 }
              gap={ "21.5px" }
            >
              <Stack
                tabIndex={ -1 }
                direction={ "row" }
                gap={ "20px" }
                alignItems={ "center" }
              >
                <FormLabelTypography
                  aria-hidden="true"
                >
                  { t("calendar_date") }
                </FormLabelTypography>
                <LocalizationProvider dateAdapter={ AdapterDateFns } adapterLocale={ locale }>
                  <DesktopDatePicker
                    aria-labelledby={ t("calendar_date") }
                    className="datepicker_wrapper"
                    disabled={ availableHours != undefined && availableHours == 0 }
                    disableHighlightToday={ true }
                    open={ isOpen }
                    onViewChange={ (newView) => setView(newView) }
                    closeOnSelect={ true }
                    format={ "dd MMMM yyyy" }
                    defaultValue={ selected }
                    disablePast={ true }
                    onChange={ (newDate) => {
                      setConflictText(false);
                      setSelected(newDate ?? new Date());
                      setFromTime("0");
                      setStartDate(undefined);
                      setToTime("0");
                      setEndDate(undefined);
                    } }
                    onYearChange={ (newDate) => {
                      setConflictText(false);
                      setSelected(newDate ?? new Date());
                      setFromTime("0");
                      setStartDate(undefined);
                      setToTime("0");
                      setEndDate(undefined);
                    } }
                    onClose={ () => {
                      setIsOpen(false);
                    } }
                    onOpen={ () => {
                      setIsOpen(true);
                    } }
                    slots={ {
                      openPickerIcon: openPickerIconComponent,

                      leftArrowIcon: () => (
                        <ChevronLeft color={ theme.customColors.exploreCardText } />
                      ),
                      rightArrowIcon: () => (
                        <ChevronRight color={ theme.customColors.exploreCardText } />
                      )
                    } }
                    slotProps={ {
                      nextIconButton: {
                        "aria-label": t("calendar_next_month"),
                        "title": t("calendar_next_month")
                      },
                      previousIconButton: {
                        "aria-label": t("calendar_prev_month"),
                        "title": t("calendar_prev_month")
                      },
                      switchViewButton: {
                        "aria-label": labelSwitchView,
                        "tabIndex": 0
                      },
                      openPickerButton: {
                        sx: {
                          "&.Mui-focusVisible": {
                            outline: `2px solid ${theme.customColors.headerIcon}`,
                            outlineOffset: "2px"
                          }
                        }
                      },
                      openPickerIcon: {
                        isOpen: isOpen
                      },
                      popper: {
                        className: "datepicker_popper",
                        placement: "bottom-start",
                        sx: {
                          margin: "4px 0 !important",
                          width: isMobile ? "auto" : "343px",

                          "& .MuiPickersPopper-paper": {
                            backgroundColor: theme.customColors.drawerWidgetBg,
                            borderRadius: "2px",
                            padding: "16px 14px"
                          },

                          ".MuiDateCalendar-root": {
                            margin: "0 0 0 auto",
                            maxHeight: "100%",

                            [theme.breakpoints.down("sm")]: {
                              margin: "0 auto",
                              width: "auto !important"
                            }
                          },
                          "& .MuiPickersCalendarHeader-switchViewButton": {
                            display: "inline-flex !important",
                            pointerEvents: "all",             // ← re-enable pointer events (parent has none)
                            width: "1px",
                            height: "1px",
                            minWidth: "unset",
                            overflow: "hidden",
                            padding: "0",
                            margin: "0",
                            opacity: 0,                       // ← fully invisible
                            "& svg": {
                              opacity: 0
                            },
                            "&:focus-visible": {
                              width: "2.5rem",
                              height: "2.5rem",
                              opacity: 1,
                              overflow: "visible",
                              outline: `2px solid ${theme.customColors.exploreCardText}`,
                              outlineOffset: "-2px",
                              borderRadius: "2px"
                            }
                          },
                          ".MuiPickersCalendarHeader-root": {
                            margin: "0",
                            padding: "0",
                            position: "relative",
                            maxHeight: "unset",
                            textTransform: "capitalize",
                            display: "flex",           // ← add this
                            alignItems: "center",      // ← add this
                            justifyContent: "center"
                          },

                          ".MuiPickersCalendarHeader-labelContainer": {
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            pointerEvents: "none",
                            position: "absolute",
                            left: "50%",
                            transform: "translateX(-50%)",
                            zIndex: 0,
                            whiteSpace: "nowrap",
                            gap: "0"                         // ← no gap between label and button
                          },

                          ".MuiPickersCalendarHeader-label": {
                            color: theme.customColors.exploreCardText,
                            fontSize: "14px",
                            fontWeight: "700",
                            lineHeight: "16.94px",
                            textAlign: "center",
                            pointerEvents: "none"
                          },

                          ".MuiPickersArrowSwitcher-root": {
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                            position: "relative",
                            zIndex: 1,
                            "& button": {
                              width: "2.5rem",
                              height: "2.5rem"
                            }
                          },

                          ".MuiDayCalendar-weekDayLabel": {
                            color: theme.customColors.systemSecondary01,
                            fontSize: "12px",
                            fontWeight: "700",
                            height: "24.12px"
                          },

                          // ".MuiPickersDay-hiddenDaySpacingFiller": {
                          //   height: "1.5625rem",
                          //   width: "1.5625rem"
                          // },

                          ".MuiDayCalendar-slideTransition": {
                            // minHeight: "15rem"
                          },

                          ".MuiYearCalendar-root": {
                            height: "240px",
                            width: "100%"
                          },

                          ".MuiDayCalendar-monthContainer": {
                            display: "flex",
                            flexDirection: "column",
                            gap: "0.4rem",
                            padding: theme.spacing(0.8, 0, 1.5, 0)
                          },

                          ".MuiDayCalendar-header": {
                            color: theme.customColors.exploreCardText,
                            justifyContent: "space-around",
                            padding: theme.spacing(0, 1)
                          },

                          ".MuiDayCalendar-weekContainer": {
                            justifyContent: "space-around",
                            padding: theme.spacing(0, 1),
                            "button": {
                              "&.Mui-disabled": {
                                color: theme.customColors.textDisabled
                              },
                              "&.Mui-selected": {
                                backgroundColor: theme.customColors.accentVariantA,
                                color: theme.customColors.drawerWidgetBg
                              }
                            }
                          },

                          "& .MuiPickersDay-root": {
                            color: theme.customColors.exploreCardText,
                            fontSize: "12px",
                            fontWeight: "700",
                            height: "25px",
                            lineHeight: "24.12px",
                            width: "25px",

                            "&.Mui-disabled": {
                              color: theme.customColors.textDisabled
                            },
                            "&:not(.Mui-selected):hover": {
                              backgroundColor: `rgba(${theme.customColors.backgroundSecondaryHover})`
                            },
                            "&.Mui-selected": {
                              backgroundColor: theme.customColors.accentVariantA,
                              color: theme.customColors.drawerWidgetBg
                            }
                          },

                          "& .MuiPickersYear-yearButton": {
                            color: theme.customColors.exploreCardText,
                            fontSize: "12px",
                            fontWeight: "700",
                            height: "25px",
                            lineHeight: "24.12px",

                            "&.Mui-disabled": {
                              color: theme.customColors.textDisabled
                            },
                            "&:focus": {
                              backgroundColor: theme.customColors.accentVariantA
                            },
                            "&:not(.Mui-selected):hover": {
                              border: "none",
                              backgroundColor: `rgba(${theme.customColors.backgroundSecondaryHover})`
                            }
                          },
                          "& .MuiPickersArrowSwitcher-button.Mui-focusVisible": {
                            outline: "none",
                            boxShadow: `0 0 0 2px ${theme.customColors.headerIcon}`
                          }
                        }
                      },
                      textField: {
                        className: "datepicker_textfield",
                        inputProps: { "aria-label": t("calendar_date") },
                        InputProps: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <Calendar size="1rem" />
                            </InputAdornment>
                          )
                        },
                        sx: {
                          flexGrow: "1",

                          "& .MuiInputBase-root": {
                            backgroundColor: "transparent",
                            borderRadius: "0.5rem",
                            border:"0.0625rem solid " + theme.customColors.border,
                            color: theme.customColors.exploreCardText,
                            fontSize: "14px",
                            fontWeight: "400",
                            height: "2.5rem",
                            lineHeight: "18px",
                            padding: "0.5rem 0.75rem 0.5rem 0.75rem",
                            "& svg":{
                              stroke: theme.customColors.exploreCardText
                            }
                          },

                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "none"
                          },

                          "& .MuiButtonBase-root": {
                            marginRight: "0 !important",
                            padding: "0 !important"
                          }
                        }
                      }
                    } }
                  />
                </LocalizationProvider>
              </Stack>
              <Stack
                gap={ "8px" }
              >
                <Stack
                  tabIndex={ -1 }
                  direction={ "row" }
                  gap={ "1rem" }
                  flexGrow={ "1" }
                  alignItems={ "center" }
                >
                  <FormLabelTypography
                    aria-hidden="true"
                  >
                    { t("calendar_hour") }
                  </FormLabelTypography>
                  { /* from */ }
                  <Stack
                    flexDirection={ "row" }
                    justifyContent={ "center" }
                    gap={ "0.5rem" }
                    alignItems={ "center" }
                    width="50%"
                  >
                    <FormSubLabelTypography
                      aria-hidden="true"
                    >
                      { t("calendar_from") }
                    </FormSubLabelTypography>
                    <Stack
                      tabIndex={ -1 }
                      aria-label={ t("select_start_time") }
                      gap={ "4px" }
                      flexGrow={ "1" }
                    >
                      <SelectTime
                        type="from"
                        value={ fromTime }
                        onChange={ (event: SelectChangeEvent) => {
                          setConflictText(false);
                          setFromTime(event.target.value);
                          const timezone = format(selected, "xxx");
                          setStartDate(format(selected, `yyyy-MM-dd'T'${event.target.value}:00.000${timezone}`));
                          setToTime("0");
                          setEndDate(undefined);
                        } }
                        options={ startOptions }
                        disabled={ availableHours != undefined && availableHours == 0 }
                      />
                    </Stack>
                  </Stack>

                  { /* to */ }
                  <Stack
                    flexDirection={ "row" }
                    justifyContent={ "center" }
                    gap={ "0.5rem" }
                    alignItems={ "center" }
                    width="50%"
                  >
                    <FormSubLabelTypography
                      aria-hidden="true"
                    >
                      { t("calendar_to") }
                    </FormSubLabelTypography>
                    <Stack
                      tabIndex={ -1 }
                      gap={ "4px" }
                      flexGrow={ "1" }
                    >
                      <SelectTime
                        type="to"
                        value={ toTime }
                        onChange={ (event: SelectChangeEvent) => {
                          setConflictText(false);
                          setToTime(event.target.value);
                          const timezone = format(selected, "xxx");
                          setEndDate(format(selected, `yyyy-MM-dd'T'${event.target.value}:00.000${timezone}`));
                        } }
                        options={ endOptions }
                        disabled={ availableHours != undefined && availableHours == 0 }
                      />
                    </Stack>
                  </Stack>
                </Stack>
                { availableHours != undefined ?
                  availableHours > 0 ? (
                    <AvailableHoursTypography
                      sx={ { marginLeft: "71px" } }
                    >
                      { t("hours_available", { count: availableHours }) }
                    </AvailableHoursTypography>
                  ) : (
                    <Stack
                      direction={ "row" }
                      gap={ "10px" }
                      sx={ { marginLeft: "71px" } }
                    >
                      <Box>
                        <Icon
                          aria-hidden="true"
                          icon={ "Icons_info" }
                          size={ "24px" }
                          color={ theme.customColors.textWarning }
                        />
                      </Box>
                      <AvailableHoursTypography
                        sx={ { color: theme.customColors.textWarning } }
                      >
                        { t("no_available_hours") }
                      </AvailableHoursTypography>
                    </Stack>
                  ) : (
                    <AvailableHoursTypography/>
                  )
                }

                {
                  conflictText ?

                    <Stack
                      direction={ "row" }
                      gap={ "10px" }
                      sx={ { marginLeft: "71px" } }
                    >
                      <Box>
                        <TriangleAlert
                          aria-hidden="true"
                          color={ theme.customColors.textWarning }
                          size="1.5rem"
                        />
                      </Box>
                      <AvailableHoursTypography
                        sx={ { color: theme.customColors.textWarning } }
                      >
                        { t("reevaluate_available_hours") }
                      </AvailableHoursTypography>
                    </Stack> : null
                }
              </Stack>
            </Stack>
            <Stack
              direction={ "row" }
              gap={ "16px" }
              justifyContent={ "center" }
            >
              <ButtonDigited typology="secondary"
                text={ t("delete_slot") }
                disabled={ startDate && endDate ? false : true }
                onClick={ ()=> {
                  setConflictText(false);
                  setFromTime("0");
                  setToTime("0");
                  setStartDate(undefined);
                  setEndDate(undefined);
                  setSelected(new Date());
                } }
              />
              <ButtonDigited
                typology="primary"
                text={ t("apply") }
                disabled={ startDate && endDate ? false : true }
                onClick={ ()=> {
                  const timeStartSelected = startOptions.find(item => item.timeOption === fromTime);
                  const timeEndSelected  = endOptions.find(item => item.timeOption === toTime);
                  if(endDate && startDate) {
                    if(
                      timeStartSelected?.bookable && timeEndSelected?.bookable
                    ){
                      mutateSlots({
                        endDate: convertToGMT(endDate),
                        slotId: slotId,
                        startDate: convertToGMT(startDate)
                      });
                    } else {
                      setConflictText(true);
                    }

                  }
                } }
              />
            </Stack>
          </Stack>
        </Stack> :
        <Stack
          role="dialog"
          aria-modal="true"
          aria-labelledby="title-to-announce-edit"
          gap={ "24px" }
        >
          <Stack
            sx={ {
              "& .icon": {
                alignSelf: "flex-end"
              }
            } }
          >
            { /* close modal button */ }
            <Stack
              id="close-modal-icon"
              className="icon"
              tabIndex={ 0 }
              role="button"
              aria-label={ t("close") }
              onClick={ ()=> {
                if(onClose){
                  onClose();
                }} }
              onKeyDown={ (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  if(onClose){
                    onClose();
                  }
                }
              } }
            >
              <X
                type="button"
                size={ "1.5rem" }
                aria-hidden="true"
                color={ theme.customColors.exploreCardText }
                cursor={ "pointer" }
              />
            </Stack>
            <Stack
              flexDirection={ "column" }
              justifyContent={ "center" }
              alignItems={ "center" }
              gap={ "1rem" }
            >
              { isLoading ? (
                <>
                  { /* nome accessibile del dialog durante il caricamento (visivamente nascosto) */ }
                  <Box
                    id="title-to-announce-edit"
                    sx={ {
                      border: 0,
                      clip: "rect(0 0 0 0)",
                      height: "1px",
                      margin: "-1px",
                      overflow: "hidden",
                      padding: 0,
                      position: "absolute",
                      whiteSpace: "nowrap",
                      width: "1px"
                    } }
                  >
                    { t("loading") }
                  </Box>
                  <Spinner
                    size={ 200 }
                    thickness={ 3 }
                    aria-hidden="true"
                  />
                </> )
                : isError ? (
                  <>
                    <AlertTriangle
                      size={ "3rem" }
                      aria-hidden="true"
                      color={ theme.customColors.exploreCardText }
                    />
                    <TitleTypography id="title-to-announce-edit">
                      { t("error_message") }
                    </TitleTypography>
                  </>
                ) : (
                  <>
                    <CircleCheck
                      size={ "3rem" }
                      aria-hidden="true"
                      color={ theme.customColors.exploreCardText }
                    />
                    <TitleTypography id="title-to-announce-edit" color={ theme.customColors.exploreCardText }>
                      { t("slot_edited") }
                    </TitleTypography>
                  </>
                )
              }
            </Stack>
          </Stack>

        </Stack>
      }
    </>
  );

  function timeSelectOptions() {
    return [
      "00:00","00:30","01:00","01:30","02:00","02:30",
      "03:00","03:30","04:00","04:30","05:00","05:30",
      "06:00","06:30","07:00","07:30","08:00","08:30",
      "09:00","09:30","10:00","10:30","11:00","11:30",
      "12:00","12:30","13:00","13:30","14:00","14:30",
      "15:00","15:30","16:00","16:30","17:00","17:30",
      "18:00","18:30","19:00","19:30","20:00","20:30",
      "21:00","21:30","22:00","22:30","23:00","23:30",
      "23:59"
    ];
  }
}

function convertToGMT(date: string) {
  const dateObj = new Date(date);
  return dateObj.toISOString();
}

const openPickerIconComponent = ({ isOpen }: {isOpen: boolean}) => (
  <OpenCalendarArrow
    isOpen={ isOpen }
  />
);
